import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    Calendar, Plus, Pencil, Trash2, Save, X, Search,
    Loader2, AlertTriangle, MapPin, Home, Sparkles, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const AutoIcon = Sparkles;

const HeatSchedulePage = () => {
    const [activeTab, setActiveTab] = useState('manual');
    const [userInfo] = useState(() => {
        const stored = localStorage.getItem('userInfo');
        return stored ? JSON.parse(stored) : null;
    });

    const tabs = [
        { id: 'manual', name: 'Ручной ввод', icon: Pencil },
        { id: 'auto', name: 'Автоматическое определение', icon: Sparkles },
    ];

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-white">График отключения ТЭЦ</h1>
                <p className="text-slate-400 text-sm mt-1">
                    Учет отключений и включений отопления по адресам и зонам
                </p>
            </div>

            <div className="flex gap-1 border-b border-slate-700 overflow-x-auto">
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-400'
                                    : 'border-transparent text-slate-400 hover:text-white'
                                }`}
                        >
                            <Icon className="w-4 h-4" />
                            {tab.name}
                        </button>
                    );
                })}
            </div>

            {activeTab === 'manual' && <ManualTab userInfo={userInfo} />}
            {activeTab === 'auto' && <AutoTab />}
        </div>
    );
};

const emptyFormData = {
    Type: 0, City: '', ZoneStreet: '', housenumber: '', DateSwitchOFF: '', DateSwitchON: ''
};

const ManualTab = ({ userInfo }) => {
    const canEdit = userInfo?.levelRight >= 1;
    const [records, setRecords] = useState([]);
    const [homes, setHomes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedCity, setSelectedCity] = useState('');

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(emptyFormData);

    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [loadingDraft, setLoadingDraft] = useState(false);

    const loadData = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const [recordsRes, homesRes] = await Promise.all([
                axios.get(
                    `${API_URL}/api/heatschedule?year=${selectedYear}${selectedCity ? `&city=${encodeURIComponent(selectedCity)}` : ''}`,
                    authHeaders()
                ),
                axios.get(`${API_URL}/api/heatschedule/homes`, authHeaders())
            ]);
            setRecords(recordsRes.data);
            setHomes(homesRes.data);
        } catch (err) {
            console.error('Ошибка загрузки:', err);
            setLoadError('Не удалось загрузить данные. Проверьте подключение.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [selectedYear, selectedCity]);

    useEffect(() => {
        if (!isFormOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape' && !saving) closeForm();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isFormOpen, saving]);

    const years = useMemo(() => {
        const y = [];
        for (let i = currentYear; i >= currentYear - 10; i--) y.push(i);
        return y;
    }, [currentYear]);

    const uniqueCities = useMemo(() => {
        return Array.from(new Set(homes.map(h => h.city).filter(Boolean))).sort();
    }, [homes]);

    // ✅ НОВОЕ: список уникальных зон из таблицы Clients__Homes
    const uniqueZones = useMemo(() => {
        return Array.from(new Set(homes.map(h => h.zone).filter(Boolean))).sort();
    }, [homes]);

    const uniqueStreets = useMemo(() => {
        return Array.from(
            new Set(
                homes
                    .filter(h => !formData.City || h.city === formData.City)
                    .map(h => h.street)
                    .filter(Boolean)
            )
        ).sort();
    }, [homes, formData.City]);

    const housesForStreet = useMemo(() => {
        if (!formData.ZoneStreet) return [];
        return homes
            .filter(h => h.street === formData.ZoneStreet && (!formData.City || h.city === formData.City))
            .map(h => h.houseNumber)
            .filter(Boolean)
            .sort();
    }, [homes, formData.ZoneStreet, formData.City]);

    const filteredRecords = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const base = q
            ? records.filter(r =>
                [r.city, r.zoneStreet, r.houseNumber, r.type === 0 ? 'адрес' : 'зона']
                    .join(' ').toLowerCase().includes(q)
            )
            : records;

        return [...base].sort((a, b) => (b.id || 0) - (a.id || 0));
    }, [records, searchQuery]);

    const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        // 1. Если пришла строка — пробуем взять первые 10 символов (YYYY-MM-DD)
        if (typeof dateStr === 'string') {
            const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
            if (match) return match[1];
        }
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            // 2. Формируем вручную из локальных компонентов — без UTC-сдвига
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch { return ''; }
    };

    const updateFormData = (patch) => {
        setFormData(prev => ({ ...prev, ...patch }));
    };

    // ✅ «Добавить запись» — пустая форма
    const openCreateForm = () => {
        setEditingId(null);
        setFormData(emptyFormData);
        setFormError('');
        setIsFormOpen(true);
    };

    const openEditForm = (record) => {
        setEditingId(record.id);
        setFormData({
            Type: record.type,
            City: record.city || '',
            ZoneStreet: record.zoneStreet || '',
            housenumber: record.houseNumber || '',
            DateSwitchOFF: formatDateForInput(record.dateSwitchOFF),
            DateSwitchON: formatDateForInput(record.dateSwitchON)
        });
        setFormError('');
        setIsFormOpen(true);
    };

    const closeForm = () => {
        if (saving) return;
        setIsFormOpen(false);
        setEditingId(null);
        setFormError('');
    };

    // ✅ «Черновик» — заполняет форму данными последней сохранённой записи
    const loadLastRecordAsDraft = async () => {
        if (loadingDraft) return;
        setLoadingDraft(true);
        try {
            const res = await axios.get(`${API_URL}/api/heatschedule/last`, authHeaders());
            const last = res.data;
            if (!last) {
                setFormError('В базе данных нет сохранённых записей');
                setTimeout(() => setFormError(''), 2500);
                return;
            }
            const loaded = {
                Type: last.type ?? 0,
                City: last.city || '',
                ZoneStreet: last.zoneStreet || '',
                housenumber: last.houseNumber || '',
                DateSwitchOFF: formatDateForInput(last.dateSwitchOFF),
                DateSwitchON: formatDateForInput(last.dateSwitchON)
            };
            setFormData(loaded);
            setEditingId(null);
            setFormError('');
            setIsFormOpen(true);
        } catch (err) {
            console.error('Ошибка загрузки черновика из БД:', err);
            setFormError(err.response?.data?.message || 'Не удалось загрузить последнюю запись');
            setTimeout(() => setFormError(''), 3000);
        } finally {
            setLoadingDraft(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!formData.ZoneStreet.trim()) {
            setFormError(formData.Type === 0 ? 'Выберите улицу' : 'Выберите зону');
            return;
        }
        if (formData.Type === 0 && !formData.housenumber.trim()) {
            setFormError('Выберите номер дома');
            return;
        }
        if (!formData.DateSwitchOFF) {
            setFormError('Укажите дату отключения');
            return;
        }

        setSaving(true);
        setFormError('');
        try {
            const payload = {
                type: formData.Type,
                city: formData.City.trim(),
                zoneStreet: formData.ZoneStreet.trim(),
                houseNumber: formData.Type === 0 ? formData.housenumber.trim() : '',
                dateSwitchOFF: formData.DateSwitchOFF || null,
                dateSwitchON: formData.DateSwitchON || null
            };
            if (editingId == null) {
                await axios.post(`${API_URL}/api/heatschedule`, payload, authHeaders());
            } else {
                await axios.put(`${API_URL}/api/heatschedule/${editingId}`, payload, authHeaders());
            }
            setIsFormOpen(false);
            setEditingId(null);
            await loadData();
        } catch (err) {
            console.error('Ошибка сохранения:', err);
            setFormError(err.response?.data?.message || 'Не удалось сохранить');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/api/heatschedule/${deleteTarget.id}`, authHeaders());
            setDeleteTarget(null);
            await loadData();
        } catch (err) {
            setLoadError(err.response?.data?.message || 'Не удалось удалить');
            setDeleteTarget(null);
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
                <Loader2 className="w-6 h-6 animate-spin" /> Загрузка данных…
            </div>
        );
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        if (typeof dateStr === 'string') {
            const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
            if (match) {
                const [y, m, d] = match[1].split('-');
                return `${d}.${m}.${y}`;
            }
        }
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}.${month}.${year}`;
        } catch { return dateStr; }
    };

    return (
        <div className="space-y-4">
            {loadError && (
                <div className="px-4 py-3 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-center justify-between animate-slide-down-fade">
                    <span className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {loadError}
                    </span>
                    <button onClick={() => setLoadError('')} className="text-red-300 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <div>
                        <h2 className="text-lg font-semibold text-white">График отключений</h2>
                        <p className="text-slate-400 text-xs mt-0.5">
                            Записей за {selectedYear} год: {filteredRecords.length}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-slate-400">Город:</label>
                        <select
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Все города</option>
                            {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-slate-400">Год:</label>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                {canEdit && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={loadLastRecordAsDraft}
                            disabled={loadingDraft}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-slate-200 hover:text-white rounded-lg transition text-sm font-medium"
                            title="Заполнить форму данными последней сохранённой записи"
                        >
                            {loadingDraft
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <FileText className="w-4 h-4" />
                            }
                            Черновик
                        </button>

                        <button onClick={openCreateForm}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition text-sm font-medium">
                            <Plus className="w-4 h-4" /> Добавить запись
                        </button>
                    </div>
                )}
            </div>

            {/* Временное сообщение после нажатия кнопки Черновик */}
            {formError && !isFormOpen && (
                <div className="px-3 py-2 bg-amber-600/20 border border-amber-600/40 rounded-lg text-amber-300 text-sm flex items-center animate-slide-down-fade">
                    <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span>{formError}</span>
                </div>
            )}

            <div className="relative">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Поиск по городу, адресу или зоне…"
                    className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {!canEdit && (
                <div className="px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-slate-400 text-sm">
                    Нет прав для редактирования графика
                </div>
            )}

            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                {filteredRecords.length === 0 ? (
                    <div className="p-8 text-center text-slate-500 text-sm">
                        Записей за {selectedYear} год не найдено
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-700/50 border-b border-slate-700">
                                <tr>
                                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Тип</th>
                                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Город</th>
                                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Адрес / Зона</th>
                                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Отключение</th>
                                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Включение</th>
                                    {canEdit && <th className="text-right px-4 py-3 text-slate-400 font-medium">Действия</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {filteredRecords.map(record => (
                                    <tr key={record.id} className="hover:bg-slate-700/30 transition">
                                        <td className="px-4 py-3">
                                            {record.type === 0 ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600/20 text-blue-400 rounded-full text-xs">
                                                    <Home className="w-3 h-3" /> Адрес
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-600/20 text-amber-400 rounded-full text-xs">
                                                    <MapPin className="w-3 h-3" /> Зона
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-slate-300">{record.city || '—'}</td>
                                        <td className="px-4 py-3 text-white">
                                            {record.type === 0
                                                ? `${record.zoneStreet}, ${record.houseNumber}`
                                                : record.zoneStreet}
                                        </td>
                                        <td className="px-4 py-3 text-red-400">{formatDate(record.dateSwitchOFF)}</td>
                                        <td className="px-4 py-3 text-green-400">{formatDate(record.dateSwitchON)}</td>
                                        {canEdit && (
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1 justify-end">
                                                    <button onClick={() => openEditForm(record)}
                                                        className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-600/20 rounded-lg transition" title="Редактировать">
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => setDeleteTarget(record)}
                                                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-600/20 rounded-lg transition" title="Удалить">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {isFormOpen && canEdit && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
                        onClick={() => !saving && closeForm()}
                    />
                    <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-[scaleIn_0.25s_cubic-bezier(0.16,1,0.3,1)]">
                        <div className="flex items-center justify-between p-5 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
                            <h3 className="text-white font-semibold flex items-center gap-2">
                                {editingId == null
                                    ? <><Plus className="w-5 h-5 text-blue-400" /> Новая запись</>
                                    : <><Pencil className="w-5 h-5 text-blue-400" /> Редактирование записи</>
                                }
                            </h3>
                            <button onClick={closeForm} disabled={saving}
                                className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-5 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-1">Тип *</label>
                                    <select value={formData.Type}
                                        onChange={(e) => updateFormData({ Type: Number(e.target.value), City: '', ZoneStreet: '', housenumber: '' })}
                                        disabled={saving}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60">
                                        <option value={0}>Адрес</option>
                                        <option value={1}>Зона</option>
                                    </select>
                                </div>

                                {formData.Type === 0 && (
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-1">Город</label>
                                        <select value={formData.City}
                                            onChange={(e) => updateFormData({ City: e.target.value, ZoneStreet: '', housenumber: '' })}
                                            disabled={saving}
                                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60">
                                            <option value="">Не указан</option>
                                            {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                )}

                                {formData.Type === 0 ? (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-1">Улица *</label>
                                            <select value={formData.ZoneStreet}
                                                onChange={(e) => updateFormData({ ZoneStreet: e.target.value, housenumber: '' })}
                                                disabled={saving} required
                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60">
                                                <option value="">Выберите улицу</option>
                                                {uniqueStreets.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-1">Номер дома *</label>
                                            <select value={formData.housenumber}
                                                onChange={(e) => updateFormData({ housenumber: e.target.value })}
                                                disabled={saving || !formData.ZoneStreet} required
                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60">
                                                <option value="">Выберите дом</option>
                                                {housesForStreet.map(h => <option key={h} value={h}>{h}</option>)}
                                            </select>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-1">Город (для зоны)</label>
                                            <select value={formData.City}
                                                onChange={(e) => updateFormData({ City: e.target.value })}
                                                disabled={saving}
                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60">
                                                <option value="">Не указан</option>
                                                {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        {/* ✅ НОВОЕ: Зона — выпадающий список из Clients__Homes.ZONE */}
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-slate-300 mb-1">Зона *</label>
                                            <select value={formData.ZoneStreet}
                                                onChange={(e) => updateFormData({ ZoneStreet: e.target.value })}
                                                disabled={saving} required
                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60">
                                                <option value="">Выберите зону</option>
                                                {uniqueZones.map(z => <option key={z} value={z}>{z}</option>)}
                                            </select>
                                        </div>
                                    </>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-1">Дата отключения *</label>
                                    <input type="date" value={formData.DateSwitchOFF}
                                        onChange={(e) => updateFormData({ DateSwitchOFF: e.target.value })}
                                        required disabled={saving}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-1">Дата включения</label>
                                    <input type="date" value={formData.DateSwitchON}
                                        onChange={(e) => updateFormData({ DateSwitchON: e.target.value })}
                                        disabled={saving}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                    />
                                </div>
                            </div>

                            {formError && (
                                <div className="px-3 py-2 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-center">
                                    <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                                    <span>{formError}</span>
                                </div>
                            )}

                            <div className="flex gap-3 pt-1 sticky bottom-0 bg-slate-800 py-2 -mx-5 px-5 border-t border-slate-700">
                                <button type="submit" disabled={saving}
                                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-medium rounded-lg transition text-sm flex items-center justify-center gap-2">
                                    {saving
                                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Сохранение…</>
                                        : <><Save className="w-4 h-4" /> {editingId == null ? 'Добавить' : 'Сохранить'}</>
                                    }
                                </button>
                                <button type="button" onClick={closeForm} disabled={saving}
                                    className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-slate-300 hover:text-white font-medium rounded-lg transition text-sm">
                                    Отмена
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !deleting && setDeleteTarget(null)} />
                    <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-[scaleIn_0.25s_cubic-bezier(0.16,1,0.3,1)]">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-600/20 flex items-center justify-center">
                            <Trash2 className="w-6 h-6 text-red-400" />
                        </div>
                        <h3 className="text-white font-semibold text-center">Удалить запись?</h3>
                        <p className="text-slate-400 text-sm text-center mt-2">
                            Запись для <span className="text-white font-medium">
                                {deleteTarget.type === 0
                                    ? `${deleteTarget.city ? deleteTarget.city + ', ' : ''}${deleteTarget.zoneStreet}, ${deleteTarget.houseNumber}`
                                    : `${deleteTarget.city ? deleteTarget.city + ', ' : ''}${deleteTarget.zoneStreet}`}
                            </span> будет удалена безвозвратно.
                        </p>
                        <div className="flex gap-3 mt-5">
                            <button onClick={confirmDelete} disabled={deleting}
                                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-medium rounded-lg transition text-sm flex items-center justify-center gap-2">
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Удалить</>}
                            </button>
                            <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                                className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-slate-300 hover:text-white font-medium rounded-lg transition text-sm">
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AutoTab = () => (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/20 flex items-center justify-center">
            <AutoIcon className="w-8 h-8 text-purple-400" />
        </div>
        <h3 className="text-white font-semibold text-lg">Автоматическое определение</h3>
        <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">
            Функция автоматического определения графиков отключения находится в разработке.
        </p>
    </div>
);

export default HeatSchedulePage;